// View-model helpers shared by the Player, Team and single-player KPI pages: a port of the design project's
// kpi/kpi-lib.js (Claude Design "KPI pages", round 6). Keep the rules here in step with that file.
import type { CSSProperties } from "react";
import type {
  WindowSpec,
  WindowSummary,
  GameInfo,
  GameSample,
  LiveGame,
  HitRate,
  KpiPlayer,
  KpiResponse,
  Mark,
  PlayerKpi,
  SeriesPoint,
  TeamKpi,
  TeamRankBasis,
} from "./types";
import { KPI_MET } from "../../../util/reportColors";

export const fmt = (v: number | null | undefined, d: number) =>
  v == null || Number.isNaN(v) ? "—" : Number(v).toFixed(d);

export const ord = (n: number | null | undefined) => {
  if (n == null) return "";
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

// Short labels: never truncate, shorten instead.
const SHORT: Record<string, string> = {
  "Rim Attempt Shot Quality": "Rim Shot Quality",
  "Opp Rim Pts / 100": "Opp Rim Pts",
  "3PT Shot Quality Allowed": "3PT Quality Allowed",
  "OREB Conceded vs Expected": "OREB Conceded vs Exp.",
  "Pick and Roll xPPP": "P&R D xPPP",
  "Deflection Rate": "Deflections",
};
export const short = (l: string) => SHORT[l] || l;

// The one data colour: met goal, the report scale's green (util/reportColors). Missed = ink outline + ×; no value =
// dashed grey. Never anything else.
export const MET = KPI_MET;
export const GPATH: Record<Mark, string> = {
  met: "M1.5,5.5 L4,8 L8.5,2.5",
  miss: "M2,2 L8,8 M8,2 L2,8",
  none: "",
};

export function blockStyle(
  m: Mark,
  padding: string,
  width?: string,
): CSSProperties {
  const base: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 5,
    padding,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(width ? { width } : {}),
  };
  if (m === "met")
    return {
      ...base,
      background: MET.bg,
      color: MET.fg,
      border: `1.5px solid ${MET.bg}`,
    };
  if (m === "miss")
    return {
      ...base,
      background: "#fff",
      color: "#1a1a1a",
      border: "1.5px solid #1a1a1a",
    };
  return {
    ...base,
    background: "#fff",
    color: "#8a8a8a",
    border: "1.5px dashed #cfccc8",
  };
}

export const mark = (
  o: { value: number | null; display?: string; metGoal?: boolean | null } | null | undefined,
): Mark =>
  !o || (o.value == null && !o.display) || o.metGoal == null
    ? "none"
    : o.metGoal
      ? "met"
      : "miss";

export const dirGlyph = (hib: boolean) => (hib ? "≥" : "≤");

export function goalText(k: {
  goal: { value: number | null; op?: string | null };
  higherIsBetter: boolean;
  decimals: number;
}) {
  if (k.goal.op) return `${k.goal.op} ${fmt(k.goal.value, 0)}`;
  return `${dirGlyph(k.higherIsBetter)} ${fmt(k.goal.value, k.decimals)}`;
}

export const unitTxt = (k: { unit: string; label: string }) =>
  k.unit === "%" || k.unit === "x" || (k.label || "").includes(k.unit)
    ? ""
    : k.unit;

export interface Hit {
  txt: string;
  short: string;
  real: boolean;
  pct: number | null;
  byGame: HitRate["byGame"];
}

export function hit(s: { hitRate?: HitRate | null } | null | undefined): Hit {
  if (s && s.hitRate && s.hitRate.games != null) {
    const p =
      s.hitRate.pct != null
        ? s.hitRate.pct
        : Math.round((100 * s.hitRate.met) / s.hitRate.games);
    return {
      txt: `Met ${s.hitRate.met} of ${s.hitRate.games}`,
      short: `${s.hitRate.met} of ${s.hitRate.games}`,
      real: true,
      pct: p,
      byGame: s.hitRate.byGame || [],
    };
  }
  return { txt: "", short: "", real: false, pct: null, byGame: [] };
}

export interface ChartModel {
  empty: boolean;
  pts: { cx: number; cy: number; fill: string; r: number; sw: number }[];
  ticks: { y: number; t: string }[];
  avg: string;
  goalY: number;
  good: { x: number; y: number; w: number; h: number };
  x0: number;
  x1: number;
  yb: number;
  xl: string;
  xr: string;
}

// Per-game chart with y-axis, goal line and 5-game rolling average.
export function chart(
  series: SeriesPoint[],
  goal: number,
  hib: boolean,
  w: number,
  h: number,
  metFill: string,
  dec = 1,
): ChartModel {
  const L = 30;
  const R = 50;
  const T = 10;
  const B = 16;
  const s = series || [];
  const vals = s.map((x) => x.value).filter((v): v is number => v != null);
  const empty: ChartModel = {
    empty: true,
    pts: [],
    ticks: [],
    avg: "",
    goalY: 0,
    good: { x: 0, y: 0, w: 0, h: 0 },
    x0: L,
    x1: w - R,
    yb: h - B,
    xl: "",
    xr: "",
  };
  if (!vals.length) return empty;
  // One y-axis rule everywhere: start at zero, end at the next nice step above the highest value or goal.
  let lo = Math.min(0, ...vals);
  let hi = Math.max(...vals, goal);
  if (hi === lo) hi = lo + 1;
  const raw = (hi - lo) / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const st = [1, 2, 2.5, 5, 10]
    .map((m) => m * mag)
    .find((m) => m >= raw) as number;
  lo = Math.floor(lo / st) * st;
  hi = Math.ceil(hi / st) * st;
  const y = (v: number) =>
    +(T + ((hi - v) / (hi - lo)) * (h - T - B)).toFixed(2);
  const n = s.length;
  const x = (i: number) =>
    +(L + (n === 1 ? 0.5 : i / (n - 1)) * (w - L - R)).toFixed(2);
  const ticks: { y: number; t: string }[] = [];
  const td = st < 1 ? (st < 0.1 ? 2 : dec > 1 ? 2 : 1) : st % 1 ? 1 : 0;
  for (let v = lo; v <= hi + 1e-9; v += st)
    ticks.push({ y: y(v), t: fmt(v, td) });
  const pts: ChartModel["pts"] = [];
  let avg = "";
  const win: number[] = [];
  s.forEach((g, i) => {
    if (g.value == null) return;
    pts.push({
      cx: x(i),
      cy: y(g.value),
      fill: g.metGoal ? metFill : "#fff",
      r: i === n - 1 ? 3.6 : 2.6,
      sw: i === n - 1 ? 1.4 : 1,
    });
    win.push(g.value);
    if (win.length > 5) win.shift();
    const m = win.reduce((a, b) => a + b, 0) / win.length;
    avg += (avg ? "L" : "M") + x(i) + "," + y(m);
  });
  const gy = y(goal);
  const good = hib
    ? { x: L, y: T, w: w - L - R, h: Math.max(0, gy - T) }
    : { x: L, y: gy, w: w - L - R, h: Math.max(0, h - B - gy) };
  return {
    empty: false,
    pts,
    ticks,
    avg,
    goalY: gy,
    good,
    x0: L,
    x1: w - R,
    yb: h - B,
    xl: shortDate(s[0]?.date),
    xr: shortDate(s[n - 1]?.date),
  };
}

export const shortDate = (d?: string) =>
  d ? d.slice(5).replace("-", "/").replace(/^0/, "") : "";

// ------------------------------------------------------------------------------------------------ view models

export interface KpiModel {
  key: string;
  label: string;
  unit: string;
  hit: Hit;
  headline: string | null;
  desc: string;
  hib: boolean;
  decimals: number;
  goal: string;
  goalV: string;
  goalP: string;
  hold: boolean;
  goalRaw: number | null;
  lastSeason: { v: string; p: string; season: string } | null;
  hasGame: boolean;
  game: {
    v: string;
    mark: Mark;
    text: string;
    unavail: boolean;
    noAtt: boolean;
  };
  std: {
    v: string;
    mark: Mark;
    small: boolean;
    p: number | null;
    pOrd: string;
    text: string;
    has: boolean;
  };
  l10: { v: string; mark: Mark; games: number; text: string; has: boolean };
  series: SeriesPoint[];
  raw: PlayerKpi;
}

export function kpiModel(k: PlayerKpi): KpiModel {
  const g = k.game;
  const s = k.seasonToDate;
  const l = k.last10;
  const hasGame = !!(g && (g.value != null || g.display) && !g.unavailable);
  return {
    key: k.key,
    label: short(k.label),
    unit: unitTxt(k),
    hit: hit(s),
    headline: k.headline,
    desc: k.description || "",
    hib: k.higherIsBetter,
    decimals: k.decimals,
    goal: goalText(k),
    goalV: fmt(k.goal.value, k.decimals),
    goalP: ord(k.goal.pctile),
    hold: !!k.goal.matchLastSeason,
    goalRaw: k.goal.value,
    lastSeason: k.lastSeason
      ? {
          v: fmt(k.lastSeason.value, k.decimals),
          p: ord(k.lastSeason.pctile),
          season: k.lastSeason.season,
        }
      : null,
    hasGame,
    game: {
      v: hasGame ? g!.display ?? fmt(g!.value, k.decimals) : "—",
      mark: mark(g),
      text: g ? g.text || "" : "",
      unavail: !!(g && g.unavailable),
      noAtt: !!(g && g.value == null && !g.display && !g.unavailable),
    },
    std: {
      v: s ? fmt(s.value, k.decimals) : "—",
      mark: mark(s),
      small: !!(s && s.smallSample),
      p: s && s.pctile != null ? s.pctile : null,
      pOrd: s && s.pctile != null ? ord(s.pctile) : "",
      text: s ? s.text || "" : "",
      has: !!s,
    },
    l10: {
      v: l ? fmt(l.value, k.decimals) : "—",
      mark: mark(l),
      games: l ? l.games : 0,
      text: l ? l.text || "" : "",
      has: !!l,
    },
    series: k.series || [],
    raw: k,
  };
}

export interface PlayerModel {
  id: string;
  playerId: string;
  name: string;
  last: string;
  emphasis: string;
  status: KpiPlayer["status"];
  played: boolean;
  twoWay: boolean;
  img: string;
  poss: number | null;
  sampleGame: string;
  noGames: boolean;
  garbageOnly: boolean;
  garbagePoss: number | null;
  tally: string;
  pm: number;
  pd: number;
  kpis: KpiModel[];
  gleague: {
    game: string | null;
    poss: number | null;
    kpis: KpiModel[];
    pm: number;
    pd: number;
  } | null;
}

export { PACKET_PAGE_COUNT, packetPageLabel } from "../../../util/packetPages";

export const headshotUrl = (nbaId: string) =>
  `https://cdn.nba.com/headshots/nba/latest/260x190/${nbaId}.png`;

export function buildPlayers(players: KpiPlayer[]) {
  let metN = 0;
  let metD = 0;
  const out: PlayerModel[] = players.map((p) => {
    const played = p.status === "played" || p.status === "sample_other_team";
    let pm = 0;
    let pd = 0;
    const kpis = p.kpis.map((k) => {
      const m = kpiModel(k);
      if (played && m.hasGame) {
        pd++;
        if (m.game.mark === "met") pm++;
      }
      return m;
    });
    metN += pm;
    metD += pd;
    let gl: PlayerModel["gleague"] = null;
    if (p.gleague) {
      const gk = p.gleague.kpis.map((k) => kpiModel(k));
      gl = {
        game: p.gleague.game,
        poss: p.gleague.gamePossessions,
        kpis: gk,
        pm: gk.filter((k) => k.game.mark === "met").length,
        pd: gk.filter((k) => k.hasGame).length,
      };
    }
    return {
      id: p.nbaId,
      playerId: p.playerId,
      name: p.display,
      last: p.name.split(",")[0],
      emphasis: p.emphasis,
      status: p.status,
      played,
      twoWay: p.twoWay,
      img: headshotUrl(p.nbaId),
      poss: p.gamePossessions,
      sampleGame: p.sampleGame || "",
      noGames: p.status === "no_games",
      garbageOnly: p.status === "garbage_only",
      garbagePoss: p.garbagePossessions ?? null,
      tally: pd ? `${pm} of ${pd}` : "",
      pm,
      pd,
      kpis,
      gleague: gl,
    };
  });
  return { players: out, metN, metD };
}

export interface TeamKpiModel {
  side: "off" | "def";
  group: string;
  label: string;
  unit: string;
  hit: Hit;
  definition: string;
  goal: string;
  /** The rank the goal represents ("5th"). */
  goalRank: string;
  /** The view's value and where it stands: one game's percentile of league team-games ("64th pct"), a window's
   * rank among the teams' season values ("13th"). */
  game: { v: string; mark: Mark; text: string; rank: string };
  std: { v: string; rank: string };
  raw: TeamKpi;
}

/** The team page's footnote (Trevor 2026-10-10), one line: one game is a percentile of the league's team-games, the
 * goal, the season level and a window are team ranks, and which season each reads. */
export function rankNote(r: TeamRankBasis | undefined, oneGame: boolean) {
  if (!r) return "";
  const lead = oneGame ? "Tonight: percentile of league team-games; " : "";
  if (r.season === r.goal)
    return oneGame
      ? `${lead}goal and season: team rank. All vs ${r.goal}.`
      : `Window, goal and season: team ranks vs ${r.goal}.`;
  const until = r.switchAfter ? ` (switches after ${r.switchAfter} games)` : "";
  return `${lead}${oneGame ? "goal rank" : "Window and goal ranks"}: ${r.goal}${until}. Season rank: ${r.season}.`;
}

export function buildTeam(team: TeamKpi[], windowed = false) {
  let metN = 0;
  let metD = 0;
  const sideMet = { off: [0, 0], def: [0, 0] };
  const kpis: TeamKpiModel[] = team.map((t) => {
    const g: GameSample | null = windowed && t.window ? t.window : t.game;
    const s = t.seasonToDate;
    if (g && g.value != null) {
      metD++;
      sideMet[t.side][1]++;
      if (g.metGoal) {
        metN++;
        sideMet[t.side][0]++;
      }
    }
    return {
      side: t.side,
      group: t.group,
      label: short(t.label),
      unit: unitTxt(t),
      // a window's goals met are over its own games
      hit: hit(windowed && t.window ? t.window : s),
      definition: t.definition,
      goal: goalText(t),
      goalRank: ord(t.goal.rank),
      game: {
        v: g && g.value != null ? fmt(g.value, t.decimals) : "—",
        mark: mark(g),
        text: g ? g.text || "" : "",
        rank:
          !g || g.value == null
            ? ""
            : g.pctile != null
              ? `${ord(g.pctile)} pct`
              : ord(g.rank),
      },
      std: {
        v: s ? fmt(s.value, t.decimals) : "—",
        rank: s ? ord(s.rank) : "",
      },
      raw: t,
    };
  });
  const groups: { side: "off" | "def"; group: string; kpis: TeamKpiModel[] }[] =
    [];
  for (const k of kpis) {
    let grp = groups.find((x) => x.side === k.side && x.group === k.group);
    if (!grp) {
      grp = { side: k.side, group: k.group, kpis: [] };
      groups.push(grp);
    }
    grp.kpis.push(k);
  }
  return {
    kpis,
    groups,
    off: groups.filter((g) => g.side === "off"),
    def: groups.filter((g) => g.side === "def"),
    metN,
    metD,
    offTally: `${sideMet.off[0]} of ${sideMet.off[1]}`,
    defTally: `${sideMet.def[0]} of ${sideMet.def[1]}`,
  };
}

export interface GameMeta {
  us: string;
  dateLong: string;
  w: string;
  at: string;
  opp: string;
  oppName: string;
  por: number | null;
  oppScore: number | null;
  line: string;
  gp: number | undefined;
}

export function gameMeta(game: GameInfo, gamesToDate?: number): GameMeta {
  const d = new Date(game.date + "T12:00:00");
  const dateLong = d
    .toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    .toUpperCase();
  const w = (game.score.POR ?? 0) > (game.score.OPP ?? 0) ? "W" : "L";
  const at = game.home ? "VS" : "AT";
  return {
    us: game.us?.abbr || "POR",
    dateLong,
    w,
    at,
    opp: game.opponent.abbr,
    oppName: game.opponent.name,
    por: game.score.POR,
    oppScore: game.score.OPP,
    line: `${dateLong} · ${at} ${game.opponent.abbr} · ${w} ${game.score.POR}–${game.score.OPP}`,
    gp: gamesToDate,
  };
}

/** The did-not-play row's status line (garbage-only nights are labelled with their possessions: Trevor, checkpoint 2). */
export const satStatus = (p: PlayerModel) =>
  p.garbageOnly
    ? `Garbage time only · ${p.garbagePoss ?? 0} poss`
    : p.noGames
      ? "No games"
      : "DNP";

/** Where a live run stops: "Halftime", "Through Q3", "Through OT1". */
/** Where a live run stops (Trevor 2026-10-05): Q1, Halftime, Through Q3, End of regulation (the immediately-postgame
 * view, minutes before the end-of-game update), OT, 2OT ... */
export const periodLabel = (p: number) =>
  p <= 1
    ? "Q1"
    : p === 2
      ? "Halftime"
      : p === 3
        ? "Through Q3"
        : p === 4
          ? "End of regulation"
          : p === 5
            ? "OT"
            : `${p - 4}OT`;

const shortMonth = (d: string) =>
  new Date(d + "T12:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

/** A multi-game window's name and dates: "Last 10 games", "Mar 28 – Apr 12" (Trevor 2026-10-02). */
export function windowTitle(
  spec: WindowSpec | undefined,
  sum: WindowSummary | null | undefined,
) {
  if (!spec || spec.kind === "game") return null;
  const n = sum?.games ?? 0;
  const name =
    spec.kind === "last"
      ? `Last ${spec.n} games`
      : spec.kind === "season"
        ? `Season · ${n} games`
        : spec.kind === "games"
          ? `Custom · ${n} games`
          : `Custom range · ${n} games`;
  const range = sum ? `${shortMonth(sum.from)} – ${shortMonth(sum.to)}` : "";
  return { name, range, line: range ? `${name} · ${range}` : name };
}

/** A live page has no result yet: the period stands where W / L would ("HALFTIME 64–65"). */
export function liveMeta(g: GameMeta, throughPeriod?: number): GameMeta {
  if (!throughPeriod) return g;
  const lbl = periodLabel(throughPeriod).toUpperCase();
  return {
    ...g,
    w: lbl,
    line: `${g.dateLong} · ${g.at} ${g.opp} · ${lbl} ${g.por}–${g.oppScore}`,
  };
}

// ------------------------------------------------------------------------------------------------ live page

/** A game's listed tip in Portland time ("7:00 PM"). */
export const tipTime = (g: LiveGame) =>
  g.tipUtc
    ? new Date(g.tipUtc).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        timeZone: "America/Los_Angeles",
      })
    : null;

/** The picker's line for a game: "10/7/2026 GSW@POR · Live · Halftime" / "· Final" / "· 7:00 PM". */
export function liveGameLabel(g: LiveGame) {
  const state =
    g.state === "in_progress"
      ? g.throughPeriod
        ? `Live · ${periodLabel(g.throughPeriod)}`
        : "Live"
      : g.state === "final"
        ? "Final"
        : tipTime(g) || "Scheduled";
  return `${g.label} · ${state}`;
}

/** The game in progress, else the latest with a live run, else the next game. */
export function defaultLiveGame(games: LiveGame[]): LiveGame | undefined {
  return (
    games.find((g) => g.state === "in_progress") ||
    games.find((g) => g.computedAt) ||
    games.find((g) => g.state === "scheduled")
  );
}

/** The selected window replaces "tonight": each KPI's game block becomes its window block, and a player who had no
 * games in the window reads as not having played in it. */
export function asWindow(data: KpiResponse): KpiResponse {
  const swap = (k: PlayerKpi): PlayerKpi =>
    k.window
      ? {
          ...k,
          game: k.window.games
            ? {
                value: k.window.value,
                metGoal: k.window.metGoal,
                n: k.window.n,
                text: k.window.text,
              }
            : null,
        }
      : k;
  const players = (data.players || []).map((p): KpiPlayer => {
    const kpis = p.kpis.map(swap);
    const any = p.kpis.some((k) => (k.window?.games || 0) > 0);
    return {
      ...p,
      kpis,
      status: any ? "played" : p.status === "no_games" ? "no_games" : "dnp",
      gamePossessions: null,
    };
  });
  return { ...data, players };
}
